import { SubProductsIgnoreMultiMenuAccountLogic } from "@constants/products";
import { AnalysisDTO } from "@models/platform-analysis/entities/analysis";
import { BusinessMetricCategories } from "@models/platform-analysis/entities/steps/businessMetric";
import { AnalysisGetFeatureSectionsDTO } from "@models/platform-analysis/entities/steps/feature";
import {
  AnalysisFeeCalculatorDTO,
  AnalysisFeeContainerDTO,
  AnalysisFeeDTO,
  ExportableAnalysisStepFeeDTO,
  FeeDetailDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import {
  FeeAnalysisLabelConstants,
  SMAFeesColumnTooltipConstants,
} from "@pages/platform-analysis/_id/steps/fee/analysis/constants";
import { sortAscByOrder } from "@utils/common";
import { isEmpty, isNil, isNumber } from "lodash";

const { Feature, BusinessMetric, Fee } = AnalysisStep;
export const compilePlatformAnalysis = (analysis: AnalysisDTO): AnalysisDTO => {
  const hasSteps = [];
  let hasMainProduct = false;

  if (analysis.setup) {
    hasMainProduct = !isEmpty(analysis.setup.mainProductId);
    if (analysis.setup.hasFeatureStep) hasSteps.push(Feature);
    if (analysis.setup.hasBusinessMetricStep) hasSteps.push(BusinessMetric);
    if (analysis.setup.hasFeeStep) hasSteps.push(Fee);

    analysis.setup.hasSteps = hasSteps;
    analysis.setup.hasMainProduct = hasMainProduct;
  }

  if (analysis.fee) {
    const isExcludePerformanceInSMATable =
      analysis?.fee?.excludePerformanceInSMATable;
    compileFeeAnalysis(analysis.fee, isExcludePerformanceInSMATable);
  }

  if (analysis.summary) {
    if (analysis.summary.feeAnalysis) {
      const isExcludePerformanceInSMATable =
        analysis.summary?.excludePerformanceInSMATable;
      compileFeeAnalysis(
        analysis.summary.feeAnalysis,
        isExcludePerformanceInSMATable,
      );
    }
  }

  return analysis;
};

export const compileFeatureSelection = (
  featureSelections: AnalysisGetFeatureSectionsDTO,
): AnalysisGetFeatureSectionsDTO => {
  if (featureSelections.sections) {
    featureSelections.sections = sortAscByOrder(featureSelections.sections);
    featureSelections.sections.forEach((section) => {
      //Sort Sub Section
      if (section.subSections) {
        section.subSections = sortAscByOrder(section.subSections) as [];
        //Sort feature of sub sections
        section.subSections?.forEach((subSection) => {
          if (subSection.features)
            subSection.features = sortAscByOrder(subSection.features);
        });
      }
    });
  }

  return featureSelections;
};

export const compileBusinessMetricSelection = (
  businessMetrics: BusinessMetricCategories,
): BusinessMetricCategories => {
  if (businessMetrics.financialStrength) {
    businessMetrics.financialStrength = sortAscByOrder(
      businessMetrics.financialStrength,
    );
  }

  if (businessMetrics.esg) {
    businessMetrics.esg = sortAscByOrder(businessMetrics.esg);
  }

  if (businessMetrics.operational) {
    businessMetrics.operational = sortAscByOrder(businessMetrics.operational);
  }

  return businessMetrics;
};

export const compileFeeAnalysis = (
  analysisStepFee: ExportableAnalysisStepFeeDTO,
  isExcludePerformanceInSMATable = false,
): AnalysisFeeDTO => {
  if (analysisStepFee.all) {
    compileFeeAnalysisContainerData({
      containerData: analysisStepFee.all,
      portfolioSetup: analysisStepFee.all.portfolioSetup,
      isExcludePerformanceInSMATable,
    });
  }

  if (analysisStepFee.shortlisted) {
    compileFeeAnalysisContainerData({
      containerData: analysisStepFee.shortlisted,
      portfolioSetup: analysisStepFee.shortlisted.portfolioSetup,
      isExcludePerformanceInSMATable,
    });
  }

  if (analysisStepFee.disqualified) {
    compileFeeAnalysisContainerData({
      containerData: analysisStepFee.disqualified,
      portfolioSetup: analysisStepFee.disqualified.portfolioSetup,
      isExcludePerformanceInSMATable,
    });
  }

  return analysisStepFee;
};

export const compileFeeCalculator = (
  calculator: AnalysisFeeCalculatorDTO,
): AnalysisFeeCalculatorDTO => {
  if (calculator.familyMembers) {
    calculator.familyMembers = sortAscByOrder(calculator.familyMembers);
    calculator.familyMembers.forEach((member) => {
      member.familyMemberAccounts = sortAscByOrder(member.familyMemberAccounts);
    });
  }

  return calculator;
};

const compileFeeAnalysisContainerData = ({
  containerData,
  portfolioSetup,
  isExcludePerformanceInSMATable,
}: {
  containerData: AnalysisFeeContainerDTO;
  portfolioSetup: FeeCalculatorPortfolioLevel;
  isExcludePerformanceInSMATable: boolean;
}) => {
  const platformOrderMap = new Map<string, number>();
  const isFamilyLevel = portfolioSetup === FeeCalculatorPortfolioLevel.Family;

  const isShowIDPSAdmin = containerData.subProducts.some(
    (subProduct) =>
      subProduct.detail.idpsAdminAndAccFees &&
      subProduct.detail.idpsAdminAndAccFees > 0,
  );

  const isShowSuperAdmin = containerData.subProducts.some(
    (subProduct) =>
      subProduct.detail.superAdminAndAccFees &&
      subProduct.detail.superAdminAndAccFees > 0,
  );

  containerData.selectedProducts.forEach((product) => {
    platformOrderMap.set(product.id, product.order);
  });

  // Sort sub-product
  containerData.subProducts = containerData.subProducts.sort((a, b) => {
    const productOrderA = platformOrderMap.get(a.productId);
    const productOrderB = platformOrderMap.get(b.productId);
    if (productOrderA && productOrderB && productOrderA !== productOrderB) {
      return productOrderA - productOrderB;
    }

    return a?.order - b?.order;
  });

  containerData.subProducts = containerData.subProducts.map((subProduct) => {
    const feeDetails: FeeDetailDTO[] = [];
    if (!subProduct.detail) return { ...subProduct, feeDetails: [] };
    const {
      totalFees,
      totalFeesExcPerf,
      totalFeesPercentage,
      totalFeesExcPerfPercentage,
      idpsAdminAndAccFees,
      idpsAdminAndAccFeesPercentage,
      superAdminAndAccFees,
      superAdminAndAccFeesPercentage,
      transactionFees,
      transactionFeesPercentage,
      intlSecuritiesOrBondHoldingFees,
      intlSecuritiesOrBondHoldingFeesPercentage,
      cashFees,
      cashFeesPercentage,
      expenseRecoveryAndOtherFees,
      expenseRecoveryAndOtherFeesPercentage,
      netInterestEarnedOnCash,
      netInterestEarnedOnCashPercentage,
      pysRefund,
      pysRefundPercentage,
      smaPortfolioFee,
      smaPortfolioFeeExcPerf,
      smaPortfolioFeePercentage,
      smaPortfolioFeeExcPerfPercentage,
    } = subProduct.detail;
    if (isNumber(totalFees) && isNumber(totalFeesPercentage)) {
      feeDetails.push({
        name: isFamilyLevel
          ? FeeAnalysisLabelConstants.TotalPlatformAndSMAFees
          : FeeAnalysisLabelConstants.TotalFees,
        dollar: totalFees,
        dollarExcPerf: totalFeesExcPerf,
        percentage: totalFeesPercentage,
        percentageExcPerf: totalFeesExcPerfPercentage,
      });
    }

    if (
      isNumber(idpsAdminAndAccFees) &&
      isNumber(idpsAdminAndAccFeesPercentage) &&
      isShowIDPSAdmin
    ) {
      feeDetails.push({
        name: FeeAnalysisLabelConstants.IDPSAdminAndAccountFees,
        dollar: idpsAdminAndAccFees,
        percentage: idpsAdminAndAccFeesPercentage,
      });
    }

    if (
      isNumber(superAdminAndAccFees) &&
      isNumber(superAdminAndAccFeesPercentage) &&
      isShowSuperAdmin
    ) {
      feeDetails.push({
        name: FeeAnalysisLabelConstants.SuperAdminAndAccountFees,
        dollar: superAdminAndAccFees,
        percentage: superAdminAndAccFeesPercentage,
      });
    }

    if (
      isNumber(intlSecuritiesOrBondHoldingFees) &&
      isNumber(intlSecuritiesOrBondHoldingFeesPercentage)
    ) {
      feeDetails.push({
        name: FeeAnalysisLabelConstants.InternationalAndOtherHoldingFees,
        dollar: intlSecuritiesOrBondHoldingFees,
        percentage: intlSecuritiesOrBondHoldingFeesPercentage,
      });
    }

    if (
      isNumber(expenseRecoveryAndOtherFees) &&
      isNumber(expenseRecoveryAndOtherFeesPercentage)
    ) {
      feeDetails.push({
        name: FeeAnalysisLabelConstants.ExpenseRecoveryAndOtherFees,
        dollar: expenseRecoveryAndOtherFees,
        percentage: expenseRecoveryAndOtherFeesPercentage,
      });
    }

    if (isNumber(transactionFees) && isNumber(transactionFeesPercentage)) {
      feeDetails.push({
        name: isFamilyLevel
          ? FeeAnalysisLabelConstants.TransactionFeesNonSMA
          : FeeAnalysisLabelConstants.TransactionFees,
        dollar: transactionFees,
        percentage: transactionFeesPercentage,
      });
    }

    if (
      isNumber(smaPortfolioFee) &&
      isNumber(smaPortfolioFeePercentage) &&
      isFamilyLevel
    ) {
      feeDetails.push({
        name: isExcludePerformanceInSMATable
          ? FeeAnalysisLabelConstants.SMAFeesExcPerf
          : FeeAnalysisLabelConstants.SMAFees,
        dollar: smaPortfolioFee,
        dollarExcPerf: smaPortfolioFeeExcPerf,
        percentage: smaPortfolioFeePercentage,
        percentageExcPerf: smaPortfolioFeeExcPerfPercentage,
        tooltip: {
          title: SMAFeesColumnTooltipConstants.Title,
          description: SMAFeesColumnTooltipConstants.Description,
        },
        isSmaFees: true,
      });
    }

    if (isNumber(pysRefund) && isNumber(pysRefundPercentage)) {
      feeDetails.push({
        name: FeeAnalysisLabelConstants.PYSRefund,
        dollar: pysRefund,
        percentage: pysRefundPercentage,
        isPysRefund: true,
      });
    }

    if (isNumber(cashFees) && isNumber(cashFeesPercentage)) {
      feeDetails.push({
        name: FeeAnalysisLabelConstants.CashFees,
        dollar: cashFees,
        percentage: cashFeesPercentage,
      });
    }

    if (
      isNumber(netInterestEarnedOnCash) &&
      isNumber(netInterestEarnedOnCashPercentage)
    ) {
      feeDetails.push({
        name: FeeAnalysisLabelConstants.NetInterestEarnedOnCash,
        dollar: netInterestEarnedOnCash ?? null,
        percentage: netInterestEarnedOnCashPercentage ?? null,
      });
    }

    // Fill exc perf empty value with normal value - critical for line chart
    feeDetails.forEach((detail) => {
      if (isNil(detail.dollarExcPerf)) detail.dollarExcPerf = detail.dollar;
      if (isNil(detail.percentageExcPerf))
        detail.percentageExcPerf = detail.percentage;
    });

    const shouldIgnoreMultiMenu =
      !subProduct.isRateCard &&
      SubProductsIgnoreMultiMenuAccountLogic.some(
        (item) =>
          item.productName === subProduct.productName &&
          item.subProductName === subProduct.name,
      );

    return {
      ...subProduct,
      id:
        subProduct.id +
        (subProduct.rateCardVersion ? `|${subProduct.rateCardVersion}` : ""),
      feeDetails,
      multiMenuAccounts:
        !shouldIgnoreMultiMenu && !!subProduct.multiMenuAccounts,
    };
  });
};
